/**
 * The one clock module (twin slice 1, plan W3 step 6; conflict X13).
 *
 * Three tickers had grown across the console, each with its own
 * `setInterval(() => setNow(Date.now()), …)`. Slice 1 adds receipt
 * ages, boundary flips and duration text in four workstreams, and a
 * fourth and fifth copy is how two cards end up disagreeing about the
 * same minute. So: one module, three hooks.
 *
 *   useNowTick(ms)       re-render every `ms` (default 1 s), for
 *                        "heard N s ago";
 *   useBoundaryTick(ms)  re-render ONCE, `ms` from now, for a liveness
 *                        boundary (`liveness.msUntilNextBoundary`) --
 *                        a quiet→lost flip with no new data;
 *   useMinuteTick()      re-render on each wall-clock minute, for
 *                        duration and "since HH:MM" text.
 *
 * ALL THREE PAUSE WHILE THE PAGE IS HIDDEN and fire at once when it is
 * shown again. A hidden tab's timers are throttled anyway; ticking it
 * only burns an all-shift iPad's battery, and the first frame after
 * showing must not be a stale one.
 *
 * WHICH CLOCK. The ticks only cause a re-render. What a component then
 * reads decides the clock: a RECEIPT AGE (is this robot still
 * reporting?) is measured on `monotonicNow()`, which a wall-clock step
 * cannot move; TEXT about a time of day ("since 14:02", "6 min") is
 * `Date.now()`, which is what the two hooks that return a value give.
 */
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';

/**
 * A monotonic millisecond clock: `performance.now()` where the platform
 * has it, `Date.now()` otherwise. Only differences between two readings
 * mean anything. Note `performance.now()` may not advance while the
 * device sleeps on some platforms, which is one reason the wake path
 * (`useLiveData`) resets receipt counts rather than trusting an age
 * measured across a sleep.
 */
export function monotonicNow(): number {
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();
}

const MINUTE_MS = 60_000;

function documentHidden(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'hidden';
}

function subscribeVisibility(onChange: () => void): () => void {
  if (typeof document === 'undefined') return () => undefined;
  document.addEventListener('visibilitychange', onChange);
  return () => document.removeEventListener('visibilitychange', onChange);
}

/** True while the page is visible; re-renders when that changes. */
export function usePageVisible(): boolean {
  return !useSyncExternalStore(subscribeVisibility, documentHidden, () => false);
}

/** True on the render where the page has just been shown again (not on
 *  mount): the moment a paused ticker owes one immediate refresh. */
function useJustShown(visible: boolean): boolean {
  const was = useRef(visible);
  const shown = visible && !was.current;
  useEffect(() => {
    was.current = visible;
  }, [visible]);
  return shown;
}

/**
 * `Date.now()`, re-read every `intervalMs` while the page is visible.
 * Paused while hidden; one immediate tick when shown again.
 */
export function useNowTick(intervalMs: number = 1_000): number {
  const visible = usePageVisible();
  const justShown = useJustShown(visible);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!visible) return;
    if (justShown) setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), Math.max(1, intervalMs));
    return () => window.clearInterval(id);
    // `justShown` is read only on the transition render; listing it would
    // re-arm the interval on the render after, for nothing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, intervalMs]);
  return now;
}

/**
 * Re-render once, `delayMs` from now: the boundary-timer form. Pass
 * `msUntilNextBoundary(age)` (or the minimum over many records) and
 * recompute on every render; null means no boundary is coming (a
 * `lost` record changes only with new data). Returns a counter that
 * changes on every firing, for use as a memo dependency.
 *
 * Re-armed whenever `delayMs` changes, so a fresh receipt that moved
 * the boundary later simply moves the timer. Paused while hidden; when
 * shown again it fires at once, because the boundary may have passed
 * while nothing ran.
 */
export function useBoundaryTick(delayMs: number | null): number {
  const visible = usePageVisible();
  const justShown = useJustShown(visible);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!visible) return;
    // Shown again: one immediate re-evaluation.
    if (justShown) setTick((n) => n + 1);
    if (delayMs === null || !Number.isFinite(delayMs)) return;
    const id = window.setTimeout(() => setTick((n) => n + 1), Math.max(0, delayMs));
    return () => window.clearTimeout(id);
    // See useNowTick on `justShown`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, delayMs]);
  return tick;
}

/** Milliseconds from `nowMs` to the next wall-clock minute boundary. */
export function msUntilNextMinute(nowMs: number): number {
  const into = ((nowMs % MINUTE_MS) + MINUTE_MS) % MINUTE_MS;
  return MINUTE_MS - into;
}

/**
 * `Date.now()`, refreshed on each wall-clock minute boundary (14:02:00,
 * 14:03:00, …) rather than every 60 s from mount, so every "6 min" and
 * "since 14:02" on screen turns over together. Paused while hidden; one
 * immediate refresh when shown again.
 */
export function useMinuteTick(): number {
  const visible = usePageVisible();
  const justShown = useJustShown(visible);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!visible) return;
    let id: number | undefined;
    const arm = () => {
      id = window.setTimeout(() => {
        setNow(Date.now());
        arm();
      }, msUntilNextMinute(Date.now()));
    };
    if (justShown) setNow(Date.now());
    arm();
    return () => {
      if (id !== undefined) window.clearTimeout(id);
    };
    // See useNowTick on `justShown`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);
  return now;
}
